import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { ugcBriefGaps } from '@fluenceur/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@/components/ui/avatar';
import { useInvitationActions, useInvitationData } from '@/contexts/invitation-context';
import { InlineErrorBanner } from '@/pages/collabs/brief-templates/editor/controls/InlineErrorBanner';
import {
  DEFAULT_DURATION_MONTHS,
  DEFAULT_END_DAYS,
  DEFAULT_FIRST_CONTENT_DAYS,
  validateBriefContent,
} from '@/pages/collabs/brief-templates/_shared/brief-content';
import { CollabTermsCompensation } from './collab-terms-compensation';
import { CollabTermsDeliverables } from './collab-terms-deliverables';
import { CollabTermsSchedule } from './collab-terms-schedule';
import { CollabTermsUgcCompensation } from './collab-terms-ugc-compensation';
import { CollabTermsUsageRights } from './collab-terms-usage-rights';
import { isCompensationSendable, recipientNameFor } from './collab-wizard-helpers';
import { useCollabRecipients } from './use-collab-recipients';
import { useUgcRecipientTerms } from './use-ugc-recipient-terms';
import { CollabTermsUgcGaps } from './collab-terms-ugc-gaps';

interface CollabTermsStepProps {
  onClose: () => void;
}

/**
 * Per-recipient terms of the wizard: deliverable quantities, compensation
 * (explicit choice required), usage rights, and the three deadlines. A UGC
 * proposal keeps its brief's rights and is always paid: its compensation is the
 * fixed amount, with the creator's starting price beside it as a reference. Terms are identical for every recipient in bulk (product arbitrage
 * v1). The design defaults are seeded into the content on entry so every
 * stepper shows a concrete, persisted-on-send value.
 */
const CollabTermsStep = ({ onClose }: CollabTermsStepProps) => {
  const { t } = useTranslation();
  const {
    collabContent,
    collabName,
    collabBriefMode,
    collabType,
    respondByDays,
    compensationConfirmed,
  } = useInvitationData();
  const isUgc = collabType === 'ugc';
  const { updateCollabContent, setRespondByDays, setCompensationConfirmed, setCurrentView } =
    useInvitationActions();
  const recipients = useCollabRecipients();
  const ugcTerms = useUgcRecipientTerms();

  // A template normally constrains its deliverables (quantities-only, per the
  // design), but a template saved with none would otherwise dead-end here: the
  // send gate needs >= 1 deliverable and there'd be no way to add one. Unlock
  // composition when the brief is blank OR arrived without any deliverable.
  // Decided once on entry so the affordance doesn't flip once one is added.
  const [allowComposeDeliverables] = useState(
    () => collabBriefMode === 'blank' || (collabContent?.deliverables.length ?? 0) === 0
  );

  // Seeding is a one-time gesture, and the guard says so rather than trusting
  // the action's reference to stay stable: a second pass would re-assert a
  // default over a value the brand had just deliberately cleared, which is a
  // suggestion turning itself into a rule.
  const seeded = useRef(false);

  // Seed the design defaults once on entry, so every stepper shows what will
  // actually be sent. Usage rights ride along for a harder reason than the
  // schedule: a brief written from scratch carries no duration and no channel,
  // the send gate refuses exactly that, and only what is seeded here can be
  // corrected. What the brief already carries is never overwritten.
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    updateCollabContent((prev) => {
      const seededTimeline =
        prev.timeline.firstContentDays === null || prev.timeline.endDays === null;
      const seededUsageRights =
        prev.usageRights.durationMonths === null || prev.usageRights.channels.length === 0;
      if (!seededTimeline && !seededUsageRights) return prev;
      return {
        ...prev,
        timeline: {
          firstContentDays: prev.timeline.firstContentDays ?? DEFAULT_FIRST_CONTENT_DAYS,
          endDays: prev.timeline.endDays ?? DEFAULT_END_DAYS,
        },
        usageRights: {
          ...prev.usageRights,
          durationMonths: prev.usageRights.durationMonths ?? DEFAULT_DURATION_MONTHS,
          channels: prev.usageRights.channels.length > 0 ? prev.usageRights.channels : ['organic'],
        },
      };
    });
  }, [updateCollabContent]);

  if (!collabContent) return null;

  const missingAmount =
    (compensationConfirmed || isUgc) &&
    collabContent.compensation.type === 'fixed_payment' &&
    (collabContent.compensation.fixedPayment?.amountCents ?? 0) <= 0;
  // Two gaps behind one refusal: no product picked, or a product picked and
  // never valued. The wizard names the one the brand actually has, because the
  // controls that fix them are different.
  const giftedValidation = validateBriefContent(collabContent);
  const missingProduct =
    compensationConfirmed &&
    collabContent.compensation.type === 'gifted_product' &&
    giftedValidation.missingGiftedProduct;
  const missingProductValue =
    compensationConfirmed &&
    collabContent.compensation.type === 'gifted_product' &&
    giftedValidation.missingGiftedProductValue;

  const invalidTimeline =
    collabContent.timeline.firstContentDays !== null &&
    collabContent.timeline.endDays !== null &&
    collabContent.timeline.endDays <= collabContent.timeline.firstContentDays;

  // An accepted brief is a contract: reuse granted for no time, or over no
  // channel, grants nothing. The server refuses such a send, and this is the
  // screen that can still fix it.
  const missingUsageRights =
    collabContent.usageRights.durationMonths === null ||
    collabContent.usageRights.channels.length === 0;

  // A UGC proposal goes out on the shared UGC rule the server applies, so the
  // step never lets through what the send would refuse.
  const canContinue = isUgc
    ? ugcBriefGaps(collabContent).length === 0
    : compensationConfirmed &&
      isCompensationSendable(collabContent) &&
      !invalidTimeline &&
      !missingUsageRights &&
      collabContent.deliverables.length > 0;

  const monoIdentityId = recipients.length === 1 ? recipients[0].creatorIdentityId : null;
  const monoUgcOffer = monoIdentityId ? (ugcTerms.get(monoIdentityId)?.ugcOffer ?? null) : null;

  const recapTitle =
    recipients.length === 1
      ? t('invitations.modal.collab.wizard.terms.recapTitle', {
          name: recipientNameFor(recipients[0], t),
        })
      : t('invitations.modal.collab.wizard.terms.recapTitleBulk', { count: recipients.length });

  return (
    <Flex direction="column" gap="3">
      <HStack
        gap="3"
        bg="background"
        borderWidth="1px"
        borderColor="cardBorder"
        rounded="lg"
        px="14px"
        py="10px"
      >
        <Avatar
          size="sm"
          name={recipientNameFor(recipients[0], t)}
          src={recipients[0]?.avatarUrl ?? undefined}
        />
        <Box flex="1" minW="0">
          <Text fontFamily="heading" fontSize="13px" fontWeight="semibold" color="dark">
            {recapTitle}
          </Text>
          <Text fontSize="11.5px" color="fg.muted" lineClamp={1}>
            {t('invitations.modal.collab.wizard.terms.recapSubtitle', { name: collabName })}
          </Text>
        </Box>
      </HStack>

      <CollabTermsDeliverables
        content={collabContent}
        updateContent={updateCollabContent}
        allowCompose={allowComposeDeliverables}
      />

      {collabContent.deliverables.length === 0 && (
        <InlineErrorBanner message={t('collabs.briefTemplates.sections.deliverables.errorEmpty')} />
      )}

      {isUgc ? (
        <CollabTermsUgcCompensation
          content={collabContent}
          updateContent={updateCollabContent}
          creator={
            monoUgcOffer ? { name: recipientNameFor(recipients[0], t), offer: monoUgcOffer } : null
          }
          showAmountError={missingAmount}
        />
      ) : (
        <CollabTermsCompensation
          content={collabContent}
          updateContent={updateCollabContent}
          compensationConfirmed={compensationConfirmed}
          onConfirm={() => setCompensationConfirmed(true)}
          showAmountError={missingAmount}
          showProductError={missingProduct}
          showProductValueError={missingProductValue}
        />
      )}

      {missingAmount && (
        <InlineErrorBanner
          message={t(
            isUgc
              ? 'collabs.briefTemplates.sections.compensation.errorUgcAmount'
              : 'collabs.briefTemplates.sections.compensation.errorAmount'
          )}
        />
      )}

      {!isUgc && (
        <CollabTermsUsageRights content={collabContent} updateContent={updateCollabContent} />
      )}

      {!isUgc && missingUsageRights && (
        <InlineErrorBanner
          message={t('collabs.briefTemplates.sections.usageRights.errorRequired')}
        />
      )}

      <CollabTermsSchedule
        ugc={isUgc}
        content={collabContent}
        updateContent={updateCollabContent}
        respondByDays={respondByDays}
        onRespondByDaysChange={setRespondByDays}
      />

      {isUgc && <CollabTermsUgcGaps content={collabContent} />}

      {invalidTimeline && (
        <InlineErrorBanner message={t('collabs.briefTemplates.sections.timeline.errorOrder')} />
      )}

      <Flex justify="flex-end" gap="2" pt="1">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          colorPalette="primary"
          onClick={() => setCurrentView('collabSend')}
          disabled={!canContinue}
        >
          {t('invitations.modal.collab.wizard.terms.cta')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default CollabTermsStep;
